import React, { useState, useEffect, useRef } from 'react';
import GuideButton from '../../../common/guide/GuideButton';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Grid,
  Alert,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useThemeContext } from '../../../../contexts/ThemeContext';
import { useAuth } from '../../../../contexts/AuthContext';
import phoneCallService, { 
  CreatePackageCustomerDto, 
  PackageGroupCheckResult,
  ShippingAddressDto 
} from '../../../../services/phoneCallService';

// ============================================================================
// INTERFACES
// ============================================================================

interface AddPackageCustomerModalProps {
  open: boolean;
  onClose: () => void;
  callerPhone: string; // Gelen aramadan telefon numarası
  branchId?: string;
  onCustomerCreated?: (customer: {
    accountId: number;
    customerName: string;
    phone: string;
    address: ShippingAddressDto;
  }) => void;
}

// Türkiye illeri (basitleştirilmiş liste)
const TURKEY_CITIES = [
  'Adana', 'Adıyaman', 'Afyonkarahisar', 'Ağrı', 'Aksaray', 'Amasya', 'Ankara', 'Antalya', 
  'Ardahan', 'Artvin', 'Aydın', 'Balıkesir', 'Bartın', 'Batman', 'Bayburt', 'Bilecik', 
  'Bingöl', 'Bitlis', 'Bolu', 'Burdur', 'Bursa', 'Çanakkale', 'Çankırı', 'Çorum', 
  'Denizli', 'Diyarbakır', 'Düzce', 'Edirne', 'Elazığ', 'Erzincan', 'Erzurum', 'Eskişehir', 
  'Gaziantep', 'Giresun', 'Gümüşhane', 'Hakkari', 'Hatay', 'Iğdır', 'Isparta', 'İstanbul', 
  'İzmir', 'Kahramanmaraş', 'Karabük', 'Karaman', 'Kars', 'Kastamonu', 'Kayseri', 'Kırıkkale', 
  'Kırklareli', 'Kırşehir', 'Kilis', 'Kocaeli', 'Konya', 'Kütahya', 'Malatya', 'Manisa', 
  'Mardin', 'Mersin', 'Muğla', 'Muş', 'Nevşehir', 'Niğde', 'Ordu', 'Osmaniye', 
  'Rize', 'Sakarya', 'Samsun', 'Şanlıurfa', 'Siirt', 'Sinop', 'Şırnak', 'Sivas', 
  'Tekirdağ', 'Tokat', 'Trabzon', 'Tunceli', 'Uşak', 'Van', 'Yalova', 'Yozgat', 'Zonguldak'
];

// Adres etiketleri
const ADDRESS_LABELS = ['Ev', 'İş', 'Diğer'];

// ============================================================================
// COMPONENT
// ============================================================================

const AddPackageCustomerModal: React.FC<AddPackageCustomerModalProps> = ({
  open,
  onClose,
  callerPhone,
  branchId,
  onCustomerCreated,
}) => {
  const { colors: themeColors } = useThemeContext();
  const { userDetails } = useAuth();
  const companyId = userDetails?.companyId;

  // Form state
  const [customerName, setCustomerName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [notes, setNotes] = useState('');

  // Adres state
  const [addressLabel, setAddressLabel] = useState('Ev');
  const [city, setCity] = useState('');
  const [district, setDistrict] = useState('');
  const [address, setAddress] = useState('');
  const [mahalle, setMahalle] = useState('');
  const [cadde, setCadde] = useState('');
  const [sokak, setSokak] = useState('');
  const [apartman, setApartman] = useState('');
  const [siteAdi, setSiteAdi] = useState('');
  const [blok, setBlok] = useState('');
  const [binaNo, setBinaNo] = useState('');
  const [kat, setKat] = useState('');
  const [daireNo, setDaireNo] = useState('');

  // UI state
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Paket grubu state
  const [groupCheckLoading, setGroupCheckLoading] = useState(false);
  const [packageGroup, setPackageGroup] = useState<PackageGroupCheckResult | null>(null);
  const [creatingGroup, setCreatingGroup] = useState(false);

  // ============================================================================
  // EFFECTS
  // ============================================================================

  // Modal sadece İLK AÇILDIĞINDA telefon numarasını set et ve grup kontrolü yap.
  // Modal açık iken callerPhone değişse bile form'daki phone alanı korunur
  // (root-cause fix: yeni gelen arama esnasında kasiyerin yazdığı telefonun
  // overwrite olmasını engeller). Modal kapanınca ref reset olur.
  const initialCallerPhoneRef = useRef<string | null>(null);
  useEffect(() => {
    if (open && callerPhone && initialCallerPhoneRef.current === null) {
      setPhone(callerPhone);
      initialCallerPhoneRef.current = callerPhone;
      checkPackageGroup();
    }
    if (!open) {
      initialCallerPhoneRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, callerPhone]);

  // Modal kapandığında formu temizle
  useEffect(() => {
    if (!open) {
      resetForm();
    }
  }, [open]);

  // ============================================================================
  // FUNCTIONS
  // ============================================================================

  const resetForm = () => {
    setCustomerName('');
    setPhone('');
    setEmail('');
    setNotes('');
    setAddressLabel('Ev');
    setCity('');
    setDistrict('');
    setAddress('');
    setMahalle('');
    setCadde('');
    setSokak('');
    setApartman('');
    setSiteAdi('');
    setBlok('');
    setBinaNo('');
    setKat('');
    setDaireNo('');
    setError(null);
    setSuccess(false);
  };

  const checkPackageGroup = async () => {
    if (!companyId) return;
    
    setGroupCheckLoading(true);
    try {
      const result = await phoneCallService.checkPackageCustomerGroup(companyId);
      setPackageGroup(result);
      console.log('📦 Paket grubu kontrolü:', result);
    } catch (err: any) {
      console.error('Paket grubu kontrolü hatası:', err);
      setPackageGroup({ exists: false, message: 'Grup kontrolü yapılamadı' });
    } finally {
      setGroupCheckLoading(false);
    }
  };

  const handleCreateGroup = async () => {
    if (!companyId) return;
    
    setCreatingGroup(true);
    try {
      const result = await phoneCallService.createPackageCustomerGroup(companyId, branchId);
      setPackageGroup({ exists: true, groupId: result.groupId, groupName: 'Paket Müşterisi' });
      console.log('✅ Paket grubu oluşturuldu:', result);
    } catch (err: any) {
      console.error('Paket grubu oluşturma hatası:', err);
      setError('Paket Müşterisi grubu oluşturulamadı');
    } finally {
      setCreatingGroup(false);
    }
  };

  const validateForm = (): boolean => {
    if (!customerName.trim()) {
      setError('Müşteri adı zorunludur');
      return false;
    }
    if (!phone.trim()) {
      setError('Telefon numarası zorunludur');
      return false;
    }
    const hasStructuredAddress = mahalle.trim() || cadde.trim() || sokak.trim() || binaNo.trim();
    if (!address.trim() && !hasStructuredAddress) {
      setError('Açık adres veya detaylı adres alanlarından en az biri doldurulmalıdır');
      return false;
    }
    if (!packageGroup?.groupId) {
      setError('Paket Müşterisi grubu bulunamadı');
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!companyId) {
      setError('Şirket bilgisi bulunamadı');
      return;
    }

    if (!validateForm()) return;

    setSaving(true);
    setError(null);

    try {
      const data: CreatePackageCustomerDto = {
        BranchId: branchId,
        CustomerName: customerName.trim(),
        Phone: phone.trim(),
        Email: email.trim() || undefined,
        Notes: notes.trim() || undefined,
        GroupId: packageGroup!.groupId!,
        AddressLabel: addressLabel,
        Country: 'Türkiye',
        City: city,
        District: district.trim(),
        Address: address.trim(),
        Mahalle: mahalle.trim() || undefined,
        Cadde: cadde.trim() || undefined,
        Sokak: sokak.trim() || undefined,
        Apartman: apartman.trim() || undefined,
        SiteAdi: siteAdi.trim() || undefined,
        Blok: blok.trim() || undefined,
        BinaNo: binaNo.trim() || undefined,
        Kat: kat.trim() || undefined,
        DaireNo: daireNo.trim() || undefined,
      };

      console.log('📝 Müşteri kaydediliyor:', data);
      
      const result = await phoneCallService.createPackageCustomer(data);
      
      console.log('✅ Müşteri oluşturuldu:', result);
      
      setSuccess(true);
      
      // Callback ile parent'a bildir
      if (onCustomerCreated) {
        onCustomerCreated({
          accountId: result.accountId,
          customerName: result.customerName,
          phone: phone.trim(),
          address: result.address,
        });
      }

      // 1.5 saniye sonra kapat
      setTimeout(() => {
        onClose();
      }, 1500);

    } catch (err: any) {
      console.error('Müşteri kaydetme hatası:', err);
      
      // API'den gelen hata mesajını göster
      if (err.response?.data?.message) {
        setError(err.response.data.message);
        
        // Eğer müşteri zaten varsa, bilgiyi göster
        if (err.response.data.accountId) {
          setError(`${err.response.data.message} (ID: ${err.response.data.accountId})`);
        }
      } else {
        setError('Müşteri kaydedilemedi. Lütfen tekrar deneyin.');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) {
      onClose();
    }
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        // Backdrop ve Esc ile kazara kapanmayı engelle (data-loss koruması).
        // Sadece "X" ve "İptal" butonları (handleClose) modalı kapatabilsin.
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          return;
        }
        handleClose();
      }}
      disableEscapeKeyDown
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          bgcolor: themeColors.primary,
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 1.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonAddIcon />
          <Typography variant="h6">Yeni Paket Müşterisi</Typography>
          <GuideButton guideKey="sales.addPackageCustomer" color="inherit" size="small" tabIndex={-1} />
        </Box>
        <IconButton onClick={handleClose} disabled={saving} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        {/* Paket Grubu Uyarısı */}
        {groupCheckLoading ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            <CircularProgress size={16} sx={{ mr: 1 }} />
            Paket Müşterisi grubu kontrol ediliyor...
          </Alert>
        ) : packageGroup && !packageGroup.exists ? (
          <Alert 
            severity="warning" 
            sx={{ mb: 2 }}
            icon={<WarningIcon />}
            action={
              <Button 
                color="inherit" 
                size="small" 
                onClick={handleCreateGroup}
                disabled={creatingGroup}
              >
                {creatingGroup ? <CircularProgress size={16} /> : 'Grubu Oluştur'}
              </Button>
            }
          >
            <Typography variant="body2" fontWeight="bold">
              "Paket Müşterisi" cari grubu bulunamadı!
            </Typography>
            <Typography variant="caption">
              Müşteri kaydedebilmek için önce bu grubun oluşturulması gerekiyor.
            </Typography>
          </Alert>
        ) : packageGroup?.exists ? (
          <Alert severity="success" sx={{ mb: 2 }} icon={<CheckCircleIcon />}>
            <Typography variant="body2">
              Paket Müşterisi grubu mevcut
              <Chip 
                label={`ID: ${packageGroup.groupId}`} 
                size="small" 
                sx={{ ml: 1, height: 20, fontSize: '0.7rem' }} 
              />
            </Typography>
          </Alert>
        ) : null}

        {/* Hata Mesajı */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Başarı Mesajı */}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            ✅ Müşteri başarıyla kaydedildi!
          </Alert>
        )}

        {/* Form */}
        <Grid container spacing={2}>
          {/* MÜŞTERİ BİLGİLERİ */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <PhoneIcon sx={{ color: themeColors.primary }} />
              <Typography variant="subtitle1" fontWeight="bold">
                Müşteri Bilgileri
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Müşteri Adı *"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              placeholder="Ad Soyad"
              disabled={saving}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: themeColors.primary,
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: themeColors.primary,
                },
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Telefon *"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="05XX XXX XXXX"
              disabled={saving}
              size="small"
              InputProps={{
                startAdornment: <PhoneIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />,
              }}
              sx={{
                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: themeColors.primary,
                },
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="E-posta (Opsiyonel)"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="ornek@email.com"
              disabled={saving}
              size="small"
              type="email"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Müşteri Notu (Opsiyonel)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="VIP müşteri, kapıda nakit tercih ediyor vb."
              disabled={saving}
              size="small"
              multiline
              rows={2}
            />
          </Grid>

          {/* ADRES BİLGİLERİ */}
          <Grid item xs={12} sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <LocationOnIcon sx={{ color: themeColors.primary }} />
              <Typography variant="subtitle1" fontWeight="bold">
                Teslimat Adresi
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Adres Etiketi</InputLabel>
              <Select
                value={addressLabel}
                onChange={(e) => setAddressLabel(e.target.value)}
                label="Adres Etiketi"
                disabled={saving}
              >
                {ADDRESS_LABELS.map((label) => (
                  <MenuItem key={label} value={label}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>İl</InputLabel>
              <Select
                value={city}
                onChange={(e) => setCity(e.target.value)}
                label="İl"
                disabled={saving}
              >
                <MenuItem value=""><em>Seçilmedi</em></MenuItem>
                {TURKEY_CITIES.map((c) => (
                  <MenuItem key={c} value={c}>{c}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="İlçe"
              value={district}
              onChange={(e) => setDistrict(e.target.value)}
              placeholder="İlçe adı"
              disabled={saving}
              size="small"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Mahalle" value={mahalle} onChange={(e) => setMahalle(e.target.value)} placeholder="Mahalle adı" disabled={saving} size="small" />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Cadde" value={cadde} onChange={(e) => setCadde(e.target.value)} placeholder="Cadde adı" disabled={saving} size="small" />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Sokak" value={sokak} onChange={(e) => setSokak(e.target.value)} placeholder="Sokak adı" disabled={saving} size="small" />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Apartman / Bina Adı" value={apartman} onChange={(e) => setApartman(e.target.value)} placeholder="Apartman adı" disabled={saving} size="small" />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Site Adı" value={siteAdi} onChange={(e) => setSiteAdi(e.target.value)} placeholder="Site / Toplu konut" disabled={saving} size="small" />
          </Grid>
          <Grid item xs={4}>
            <TextField fullWidth label="Blok" value={blok} onChange={(e) => setBlok(e.target.value)} placeholder="Blok" disabled={saving} size="small" />
          </Grid>
          <Grid item xs={4}>
            <TextField fullWidth label="Bina No" value={binaNo} onChange={(e) => setBinaNo(e.target.value)} placeholder="No" disabled={saving} size="small" />
          </Grid>
          <Grid item xs={4}>
            <TextField fullWidth label="Kat" value={kat} onChange={(e) => setKat(e.target.value)} placeholder="Kat" disabled={saving} size="small" />
          </Grid>
          <Grid item xs={4}>
            <TextField fullWidth label="Daire No" value={daireNo} onChange={(e) => setDaireNo(e.target.value)} placeholder="Daire" disabled={saving} size="small" />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Açık Adres"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="Serbest metin adres (opsiyonel)"
              disabled={saving}
              size="small"
              multiline
              rows={2}
            />
          </Grid>
        </Grid>
      </DialogContent>

      {/* Actions */}
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleClose}
          disabled={saving}
          color="inherit"
        >
          İptal
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving || !packageGroup?.exists || success}
          startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <PersonAddIcon />}
          sx={{
            bgcolor: themeColors.primary,
            '&:hover': { bgcolor: themeColors.primary, filter: 'brightness(0.9)' },
          }}
        >
          {saving ? 'Kaydediliyor...' : 'Müşteriyi Kaydet'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddPackageCustomerModal;
